import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const kr = (cents) => window.avlMoney(cents);
const tr = (key, vars) => window.avlT_(`probiotika.${key}`, vars);

class AvlProbiotika extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.offers = JSON.parse(this.querySelector('[data-offers]').textContent);
    this.variantId = Number(this.dataset.variant);
    this.atc = this.querySelector('[data-atc]');
    this.atcLabel = this.querySelector('[data-atc-label]');
    this.status = this.querySelector('[data-status]');
    this.sticky = this.querySelector('[data-sticky]');
    this.setupOffers();
    this.setupGallery();
    this.setupCalc();
    this.setupUgc();
    this.setupSticky();
    this.atc.addEventListener('click', () => this.add());
    this.querySelector('[data-sticky-atc]')?.addEventListener('click', () => this.add());
  }

  get choice() { return this.querySelector('input[name="avlpb-offer"]:checked')?.value || 'bundle'; }

  setupOffers() {
    this.querySelectorAll('input[name="avlpb-offer"]').forEach((input) => input.addEventListener('change', () => this.render()));
    this.querySelectorAll('[data-plan]').forEach((btn) => btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.querySelectorAll('[data-plan]').forEach((b) => b.setAttribute('aria-checked', String(b === btn)));
      this.offers.sub.plan = Number(btn.dataset.plan);
      const sub = this.querySelector('input[value="sub"]');
      if (sub && !sub.checked) { sub.checked = true; }
      this.render();
    }));
    const checked = this.querySelector('[data-plan][aria-checked="true"]');
    if (checked) this.offers.sub.plan = Number(checked.dataset.plan);
    this.render();
  }

  render() {
    const offer = this.offers[this.choice];
    if (!this.atc.disabled || this.atcLabel.textContent !== tr('sold_out')) this.atcLabel.textContent = tr('atc_price', { price: kr(offer.price) });
    const price = this.querySelector('[data-sticky-price]');
    const label = this.querySelector('[data-sticky-label]');
    if (price) price.textContent = kr(offer.price);
    if (label) label.textContent = offer.label;
  }

  async read(url, options = {}) {
    const res = await fetch(url, { ...options, headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(data.description || data.message || tr('error_generic')); err.rejected = true; throw err; }
    return data;
  }

  async add() {
    if (this.pending) return;
    const root = window.Shopify?.routes?.root || '/';
    const choice = this.choice;
    const offer = this.offers[choice];
    const item = { id: this.variantId, quantity: offer.qty };
    if (choice === 'sub' && offer.plan) item.selling_plan = offer.plan;
    this.pending = true;
    this.atc.disabled = true; this.atc.setAttribute('aria-busy', 'true');
    this.status.textContent = tr('adding');
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const result = await this.read(`${root}cart/add.js?appstle=true`, { method: 'POST', body: JSON.stringify({ items: [item], sections, sections_url: location.pathname }) });
      const cart = await this.read(`${root}cart.js`);
      if (choice === 'sub' && !cart.items.some((i) => i.variant_id === this.variantId && i.selling_plan_allocation)) {
        this.status.textContent = tr('sub_unconfirmed');
      } else {
        this.status.textContent = '';
      }
      this.dispatchEvent(new CartAddEvent(cart, String(this.variantId), { source: 'product-form-component', productId: this.dataset.productId, variantId: String(this.variantId), itemCount: offer.qty, sections: result.sections }));
      const drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer-component');
      if (typeof drawer?.open === 'function') drawer.open(); else location.href = `${root}cart`;
    } catch (error) {
      this.status.textContent = error.message || tr('error_network');
      try { const cart = await this.read(`${root}cart.js`); this.dispatchEvent(new CartUpdateEvent(cart, this.id, { source: 'avlpb-recovery', itemCount: cart.item_count, didError: true })); } catch { /* ignore */ }
    } finally {
      this.pending = false; this.atc.disabled = false; this.atc.removeAttribute('aria-busy');
    }
  }

  setupGallery() {
    const track = this.querySelector('[data-track]');
    const thumbs = [...this.querySelectorAll('[data-thumb]')];
    if (!track || !thumbs.length) return;
    thumbs.forEach((t) => t.addEventListener('click', () => {
      track.scrollTo({ left: track.clientWidth * Number(t.dataset.thumb), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }));
    track.addEventListener('scroll', () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
    }, { passive: true });
  }

  setupCalc() {
    const rows = (this.dataset.dosage || '').split(/[\n·;]+/).map((l) => l.split('|').map((s) => parseFloat(s.replace(',', '.')))).filter((r) => r.length === 2 && r.every(Number.isFinite)).sort((a, b) => a[0] - b[0]);
    if (!rows.length) return;
    const calc = this.querySelector('[data-calc]');
    this.querySelector('[data-calc-note]').hidden = true;
    calc.hidden = false;
    const scoopG = parseFloat(this.dataset.scoopG) || 0;
    const netG = parseFloat(this.dataset.netG) || 200;
    const input = calc.querySelector('[data-weight]');
    const fmt = (n) => String(Math.round(n * 10) / 10).replace('.', ',');
    const update = () => {
      const kg = Number(input.value);
      calc.querySelector('[data-weight-out]').textContent = tr('kg', { n: kg });
      const scoops = (rows.find((r) => kg <= r[0]) || rows[rows.length - 1])[1];
      const grams = scoops * scoopG;
      calc.querySelector('[data-dose]').textContent = `${tr(scoops === 1 ? 'scoops_one' : 'scoops_other', { n: fmt(scoops) })}${grams ? ` (${fmt(grams)} g)` : ''}`;
      if (!grams) { calc.querySelector('[data-days]').textContent = '–'; return; }
      const days = Math.floor(netG / grams);
      const weeks = Math.floor(days / 7);
      calc.querySelector('[data-days]').textContent = tr(days === 1 ? 'days_one' : 'days_other', { n: days });
      const plans = [...this.querySelectorAll('[data-plan]')];
      const match = plans.map((b) => [b, parseInt(b.textContent, 10)]).filter(([, w]) => w <= weeks).pop();
      calc.querySelector('[data-tip]').textContent = match ? tr('tip', { n: match[1] }) : '';
    };
    input.addEventListener('input', update);
    update();
  }

  setupUgc() {
    this.querySelectorAll('.avlpb-ugc__item').forEach((fig) => {
      const video = fig.querySelector('video');
      const btn = fig.querySelector('[data-ugc-play]');
      if (!video || !btn) return;
      btn.addEventListener('click', () => {
        this.querySelectorAll('.avlpb-ugc__item video').forEach((v) => { if (v !== video) { v.pause(); v.closest('figure').classList.remove('is-playing'); } });
        video.muted = false; video.controls = true; video.play().catch(() => {});
        fig.classList.add('is-playing'); btn.setAttribute('tabindex', '-1');
      });
      video.addEventListener('pause', () => { fig.classList.remove('is-playing'); btn.removeAttribute('tabindex'); });
    });
  }

  setupSticky() {
    if (!this.sticky || !('IntersectionObserver' in window)) return;
    this.sticky.hidden = false;
    new IntersectionObserver(([entry]) => {
      this.sticky.classList.toggle('is-on', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(this.atc);
  }
}

if (!customElements.get('avl-probiotika')) customElements.define('avl-probiotika', AvlProbiotika);
